interface Cookie {
    name: string;
    value: string;
    size: number;
    isAMP: boolean;
}

interface CookieManagementResults {
    totalSizeBefore: number;
    totalSizeAfter: number;
    ampCookies: Cookie[];
    removedCookies: Cookie[];
    regularCookies: Cookie[];
    failedRemovals: string[];
}

/**
 * Removes a cookie by trying multiple approaches
 * @param name - Name of the cookie to remove
 * @returns boolean indicating if removal was successful
 */
function removeCookie(name: string): boolean {
    try {
        // Common paths used by AMP cookies
        const paths = ['/', '/amp', '/amp/', '', '/v/'];
        
        // Common domains for Volt Money AMP cookies
        const domains = [
            '.voltmoney.in',
            'voltmoney.in',
            '.amplitude.com',
            window.location.hostname
        ];

        // Try removing with all combinations of SameSite attributes
        const sameSiteOptions = ['Lax', 'Strict', 'None'];
        
        domains.forEach(domain => {
            paths.forEach(path => {
                sameSiteOptions.forEach(sameSite => {
                    // Standard removal
                    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=${path}; domain=${domain}; SameSite=${sameSite}`;
                    
                    // With secure flag
                    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=${path}; domain=${domain}; SameSite=${sameSite}; secure`;
                    
                    // Try with max-age
                    document.cookie = `${name}=; max-age=0; path=${path}; domain=${domain}; SameSite=${sameSite}`;
                });
            });
        });

        // Special handling for AMP cookies
        if (name.startsWith('AMP_') || name.startsWith('amp_')) {
            // Additional specific attempts for AMP cookies
            document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=.voltmoney.in; SameSite=Lax`;
            document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=.amplitude.com; SameSite=Lax`;
            
            // Try without domain specification
            document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
            
            // Try with HttpOnly flag
            document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=.voltmoney.in; HttpOnly`;
        }

    } catch (error) {
        console.error(`Error removing cookie ${name}:`, error);
        return false;
    }

    // Verify if cookie was actually removed
    const cookieStillExists = document.cookie
        .split(';')
        .some(cookie => cookie.trim().startsWith(`${name}=`));
    
    if (cookieStillExists) {
        console.warn(`Failed to remove cookie: ${name}`);
        return false;
    }

    console.log(`Successfully removed cookie: ${name}`);
    return true;
}

/**
 * Gets the current total size of all cookies
 */
function getTotalCookieSize(): number {
    return document.cookie.split(';')
        .reduce((total, cookie) => total + new Blob([cookie]).size, 0);
}

/**
 * Manages cookies to stay within load balancer limit
 * Removes AMP cookies in descending size order if total size exceeds limit
 * @param maxTotalSize - Maximum total size in bytes (default: 4000)
 */
export function manageAMPCookies(maxTotalSize: number = 4000): CookieManagementResults {
    const results: CookieManagementResults = {
        totalSizeBefore: 0,
        totalSizeAfter: 0,
        ampCookies: [],
        removedCookies: [],
        regularCookies: [],
        failedRemovals: []
    };

    // Get all cookies and their sizes
    const allCookies: Cookie[] = document.cookie.split(';')
        .map(cookie => {
            const trimmedCookie = cookie.trim();
            const [name, value] = trimmedCookie.split('=').map(part => decodeURIComponent(part));
            const size = new Blob([trimmedCookie]).size;
            return { 
                name, 
                value, 
                size, 
                isAMP: name.startsWith('amp_') || name.startsWith('AMP_') 
            };
        })
        .filter((cookie): cookie is Cookie => Boolean(cookie.name));

    // Separate AMP and regular cookies
    results.ampCookies = allCookies
        .filter(cookie => cookie.isAMP)
        .sort((a, b) => b.size - a.size);
    
    results.regularCookies = allCookies.filter(cookie => !cookie.isAMP);
    results.totalSizeBefore = allCookies.reduce((sum, cookie) => sum + cookie.size, 0);

    console.log(`Initial state:
    Total cookie size: ${results.totalSizeBefore} bytes
    Found ${results.ampCookies.length} AMP cookies`);

    // If we're over the limit, start removing AMP cookies
    if (results.totalSizeBefore > maxTotalSize) {
        console.log(`Exceeding ${maxTotalSize} bytes limit. Removing AMP cookies...`);
        
        let currentSize = results.totalSizeBefore;
        
        for (const cookie of results.ampCookies) {
            if (currentSize <= maxTotalSize) break;
            
            console.log(`Attempting to remove "${cookie.name}" (${cookie.size} bytes)`);
            
            // Try to remove the cookie and verify removal
            const removed = removeCookie(cookie.name);
            
            if (removed) {
                console.log(`✓ Successfully removed "${cookie.name}"`);
                currentSize -= cookie.size;
                results.removedCookies.push(cookie);
            } else {
                console.warn(`⚠ Failed to remove "${cookie.name}"`);
                results.failedRemovals.push(cookie.name);
            }
        }
        
        results.totalSizeAfter = currentSize;
    } else {
        results.totalSizeAfter = results.totalSizeBefore;
    }

    // Verify final state
    const finalSize = getTotalCookieSize();
    console.log('\nFinal state:');
    console.log(`Measured total size: ${finalSize} bytes`);
    console.log(`Cookies removed: ${results.removedCookies.length}`);
    console.log(`Failed removals: ${results.failedRemovals.length}`);

    if (results.failedRemovals.length > 0) {
        console.warn('Failed to remove these cookies:', results.failedRemovals);
    }

    if (finalSize > maxTotalSize) {
        console.warn(`Still exceeding limit by ${finalSize - maxTotalSize} bytes`);
    }

    return results;
}

// Example usage:
// const results = manageAMPCookies(4000);
// console.log('\nDetailed results:', results);

// Verify remaining AMP cookies
function verifyRemainingAMPCookies(): string[] {
    return document.cookie.split(';')
        .map(cookie => cookie.trim())
        .filter(cookie => {
            const name = cookie.split('=')[0].trim();
            return name.startsWith('amp_') || name.startsWith('AMP_');
        });
}